import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, Dialog, DialogContent } from "@mui/material";
import React, { useMemo, useState } from "react";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import SWR_KEYS from "@/constants/swr-keys";
import useAuth from "@/contexts/auth-context";
import useToast from "@/contexts/toast-context";
import useCreateWorkflowStep from "@/hooks/requests/workflowStep/use-create-workflow-step";
import useGetWorkflowStepById from "@/hooks/requests/workflowStep/use-get-workflow-step-by-id";
import useRemoveWorkflowStep from "@/hooks/requests/workflowStep/use-remove-workflow-step";
import useUpdateWorkflowStep from "@/hooks/requests/workflowStep/use-update-workflow-step";
import useDialog from "@/hooks/use-dialog";
import useLoading from "@/hooks/use-loading";
import useMutate from "@/hooks/use-mutate";
import { IWorkflowStep, IWorkflowStepInput } from "@/interfaces/workflow-step.interface";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { WorkflowEditDialogMessages } from "@/messages/workflow/workflow-edit-dialog.messages";
import { UserProfile } from "@/types/user.types";

import {
  getStepsPointedToByStepWithDetail,
  menuOptionReferencesStepName,
  stepPointsToAnotherStep,
} from "@/components/screens/WorkflowFormPage/constants";

import { WorkflowStepTable } from "./components/StepTable";
import WorkflowStepForm from "./components/WorkflowStepForm";
import { normalizeWorkflowStepFormData } from "./form-data";

type WorkflowStepsTabProps = {
  messages: WorkflowEditDialogMessages;
  workflow: IWorkflow;
};

const revalidateAndClose = (mutate: (key: string) => void, handleClose: () => void) => {
  mutate(SWR_KEYS.getWorkflowById);
  mutate(SWR_KEYS.getWorkflowSteps);
  mutate(SWR_KEYS.getWorkflowStepById);
  handleClose();
};

function checkStepNumberExistence(
  stepNumber: number,
  workflowSteps: IWorkflowStep[] | undefined,
  currentStepId: number | undefined,
  isEditingStep: boolean
): boolean {
  if (!workflowSteps) return true;
  const normalizedStepNumber = Number(stepNumber);
  if (Number.isNaN(normalizedStepNumber)) return false;

  const hasSameStepNumber = (step: IWorkflowStep) =>
    Number(step.stepNumber) === normalizedStepNumber;

  if (isEditingStep) {
    return !workflowSteps.some(
      (step) => step.id !== currentStepId && hasSameStepNumber(step)
    );
  }
  return !workflowSteps.some((step) => hasSameStepNumber(step));
}

function checkStepNameExistence(
  stepName: string,
  workflowSteps: IWorkflowStep[] | undefined,
  currentStepId: number | undefined,
  isEditingStep: boolean
): boolean {
  if (!workflowSteps) return true;
  const normalizedStepName = String(stepName ?? "").trim();
  if (!normalizedStepName) return false;

  const hasSameStepName = (step: IWorkflowStep) =>
    String(step.stepName ?? "").trim() === normalizedStepName;

  if (isEditingStep) {
    return !workflowSteps.some((step) => step.id !== currentStepId && hasSameStepName(step));
  }
  return !workflowSteps.some((step) => hasSameStepName(step));
}

type StepReference = {
  step: IWorkflowStep;
  viaGoto: boolean;
  viaException: boolean;
  viaMenuOption: boolean;
};

/** Retorna os steps que referenciam o step dado (goto, exceção ou opção de menu) */
function getStepsReferencingStep(
  step: IWorkflowStep,
  allSteps: IWorkflowStep[] | undefined
): StepReference[] {
  if (!allSteps || allSteps.length === 0) return [];
  const stepName = String(step.stepName || "").trim();
  if (!stepName) return [];
  return allSteps
    .filter(
      (s) =>
        s.id !== step.id &&
        (String(s.gotoWorkflowStepName || "").trim() === stepName ||
          String(s.exceptionSourceWorkflowStepName || "").trim() === stepName ||
          menuOptionReferencesStepName(s, stepName))
    )
    .map((s) => ({
      step: s,
      viaGoto: String(s.gotoWorkflowStepName || "").trim() === stepName,
      viaException:
        String(s.exceptionSourceWorkflowStepName || "").trim() === stepName,
      viaMenuOption: menuOptionReferencesStepName(s, stepName),
    }));
}

export default function WorkflowSteps({ messages, workflow }: WorkflowStepsTabProps) {
  const { user } = useAuth();
  const isAdmin =
    user?.userProfile?.some((p) => p.profileId === UserProfile.ADMINISTRATOR) ?? false;

  const { update, isLoading: isUpdating, loadingIcon } = useUpdateWorkflowStep();
  const { create, isLoading: isCreating } = useCreateWorkflowStep();
  const {
    removeWorkflowStep,
    isLoading: isLoadingRemoveWorkflowStep,
    loadingIcon: loadingIconRemove,
  } = useRemoveWorkflowStep();
  const { showToast } = useToast();
  const { startLoading, endLoading } = useLoading();
  const { mutate } = useMutate();

  const [workflowStep, setWorkflowStep] = useState<IWorkflowStep | null>(null);
  const [isEditingStep, setIsEditingStep] = useState<boolean>(false);
  const { isVisible, handleOpen, handleClose } = useDialog();
  const {
    isVisible: isDeleteConfirmVisible,
    handleOpen: handleOpenDeleteConfirm,
    handleClose: handleCloseDeleteConfirm,
    selected: stepToDelete,
  } = useDialog<IWorkflowStep>();

  const {
    stepUpdateSuccess,
    stepUpdateError,
    tabStepsDataLabel,
    workflowStepCreateSuccess,
    stepCreateError,
    deleteWorkflowStepSuccess,
    stepNumberAlreadyExist,
    stepNameAlreadyExist,
    deleteWorkflowStepDialogTitle,
    deleteWorkflowStepDialogContent,
    deleteWorkflowStepReferencedIntro,
    deleteWorkflowStepReferencedDetailGoto,
    deleteWorkflowStepReferencedDetailException,
    deleteWorkflowStepReferencedDetailBoth,
    deleteWorkflowStepReferencedDetailMenuOption,
    deleteWorkflowStepReferencedOutro,
    deleteWorkflowStepPointsToOthersIntroOnly,
    deleteWorkflowStepPointsToOthersIntroAlso,
    deleteWorkflowStepRefTypeGoto,
    deleteWorkflowStepRefTypeException,
    deleteWorkflowStepRefTypeMenuOption,
    deleteWorkflowStepReferencedFinalMessage,
    deleteWorkflowStepDialogCancel,
    deleteWorkflowStepDialogConfirm,
    deleteWorkflowStepDialogClose,
    createButton,
    noStepsMessage,
  } = messages;

  const handleUpdate = async (formDataInput: IWorkflowStepInput) => {
    if (!workflowStep || !workflow) return;

    startLoading();
    const formData = normalizeWorkflowStepFormData(formDataInput, workflow.id);
    const _id = workflowStep.id;

    await update({
      _id,
      formData,
      successMessage: stepUpdateSuccess,
      errorMessage: stepUpdateError,
    });

    revalidateAndClose(mutate, handleClose);
    endLoading();
  };

  const handleCreate = async (formDataInput: IWorkflowStepInput) => {
    if (!workflow) return;

    const formData = normalizeWorkflowStepFormData(formDataInput, workflow.id);

    await create({
      formData,
      successMessage: workflowStepCreateSuccess,
      errorMessage: stepCreateError,
      onSuccess: () => revalidateAndClose(mutate, handleClose),
    });
  };

  const handleConfirmDelete = (step: IWorkflowStep) => {
    handleOpenDeleteConfirm(step);
  };

  const handleExecuteDelete = async () => {
    if (!stepToDelete) return;

    await removeWorkflowStep({
      _id: stepToDelete.id,
      successMessage: deleteWorkflowStepSuccess,
      onSuccess: () => {
        revalidateAndClose(mutate, handleCloseDeleteConfirm);
      },
    });
  };

  const handleSubmitForm = async (formData: IWorkflowStepInput) => {
    const workflowSteps = workflow?.workflowSteps ?? [];
    const normalizedStepNumber = Number(formData.stepNumber);

    if (Number.isNaN(normalizedStepNumber) || normalizedStepNumber < 1) {
      showToast({
        text: "O número da etapa deve ser maior que zero.",
        type: "error",
      });
      return;
    }

    const isValidStepNumber = checkStepNumberExistence(
      normalizedStepNumber,
      workflowSteps,
      workflowStep?.id,
      isEditingStep
    );

    if (!isValidStepNumber) {
      showToast({ text: stepNumberAlreadyExist, type: "error" });
      return;
    }

    const isValidStepName = checkStepNameExistence(
      formData.stepName,
      workflowSteps,
      workflowStep?.id,
      isEditingStep
    );

    if (!isValidStepName) {
      showToast({ text: stepNameAlreadyExist, type: "error" });
      return;
    }

    const candidateStep: IWorkflowStep = {
      ...(workflowStep ?? ({} as IWorkflowStep)),
      ...formData,
      stepNumber: normalizedStepNumber,
      id: workflowStep?.id ?? -1,
      workflowId: workflow?.id ?? 0,
      rowDate: workflowStep?.rowDate ?? new Date(),
    };

    if (isEditingStep) {
      await handleUpdate(formData);
    } else {
      await handleCreate(formData);
    }
  };

  const handleOpenDialog = (isEditing: boolean) => {
    setIsEditingStep(isEditing);
    handleOpen();
  };

  const onOpenCreate = () => {
    setWorkflowStep(null);
    handleOpenDialog(false);
  };

  const stepIdForDetails = isVisible && isEditingStep ? (workflowStep?.id ?? 0) : 0;
  const { data: freshStep } = useGetWorkflowStepById({ _id: stepIdForDetails });

  const effectiveStep = useMemo<IWorkflowStep | null>(() => {
    if (!workflowStep) return null;
    if (!freshStep || freshStep.id !== workflowStep.id) return workflowStep;
    return { ...workflowStep, ...freshStep };
  }, [workflowStep, freshStep]);





  const isLoadingForm = isUpdating || isCreating;

  return (
    <PageContainer>
      <Card>
        <MDBox p={2}>
          <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight={"bold"}>
              {tabStepsDataLabel}
            </MDTypography>
            {isAdmin && (
              <MDButton
                endIcon={<AddCircleOutlineIcon />}
                variant="outlined"
                color="dark"
                onClick={onOpenCreate}
              >
                {createButton}
              </MDButton>
            )}
          </MDBox>

          <WorkflowStepTable
            messages={messages}
            workflowSteps={workflow?.workflowSteps}
            handleOpenDialog={handleOpenDialog}
            setWorkflowStep={setWorkflowStep}
            isAdmin={isAdmin}
            onRemove={handleConfirmDelete}
            isLoadingPopover={isLoadingRemoveWorkflowStep}
            noStepsMessage={noStepsMessage}
          />

          <Dialog
            open={isVisible}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                m: 4,
                width: "calc(100% - 64px)",
                maxWidth: 720,
                maxHeight: "calc(100% - 64px)",
              },
            }}
          >
            <DialogContent sx={{ overflowY: "auto" }}>
              <WorkflowStepForm
                messages={messages}
                submitForm={handleSubmitForm}
                isLoading={isLoadingForm}
                loadingIcon={loadingIcon}
                step={effectiveStep}
                workflow={workflow}
                isEditing={isEditingStep}
                isAdmin={isAdmin}
                onNavigateToStep={(targetStep) => {
                  setWorkflowStep(targetStep);
                  setIsEditingStep(true);
                }}
              />
            </DialogContent>
          </Dialog>

          <ConfirmationDialog
            isOpen={isDeleteConfirmVisible}
            onClose={handleCloseDeleteConfirm}
            onConfirm={handleExecuteDelete}
            titleText={deleteWorkflowStepDialogTitle}
            minWidth={
              stepToDelete &&
              getStepsReferencingStep(stepToDelete, workflow?.workflowSteps).length === 0 &&
              getStepsPointedToByStepWithDetail(stepToDelete, workflow?.workflowSteps).length === 0
                ? 520
                : undefined
            }
            maxWidth={
              stepToDelete &&
              getStepsReferencingStep(stepToDelete, workflow?.workflowSteps).length === 0 &&
              getStepsPointedToByStepWithDetail(stepToDelete, workflow?.workflowSteps).length === 0
                ? "90vw"
                : undefined
            }
            descriptionText={
              stepToDelete
                ? (() => {
                    const refs = getStepsReferencingStep(
                      stepToDelete,
                      workflow?.workflowSteps
                    );
                    const pointedTo = getStepsPointedToByStepWithDetail(
                      stepToDelete,
                      workflow?.workflowSteps
                    );
                    const pointsToOthers = pointedTo.length > 0;
                    const parts: string[] = [];
                    if (refs.length > 0) {
                      const stageLabel = messages.stageColumn;
                      const lines = refs.map((ref) => {
                        const { step: s, viaGoto, viaException, viaMenuOption } = ref;
                        let detail: string;
                        if (viaGoto && viaException)
                          detail = deleteWorkflowStepReferencedDetailBoth;
                        else if (viaGoto)
                          detail = deleteWorkflowStepReferencedDetailGoto;
                        else if (viaException)
                          detail = deleteWorkflowStepReferencedDetailException;
                        else if (viaMenuOption)
                          detail = deleteWorkflowStepReferencedDetailMenuOption;
                        else
                          detail = deleteWorkflowStepReferencedDetailGoto;
                        return `"${s.stepName}" (${stageLabel} ${s.stepNumber}). ${detail}`;
                      });
                      parts.push(deleteWorkflowStepReferencedIntro, "", ...lines);
                    }
                    if (pointsToOthers) {
                      const stageLabel = messages.stageColumn;
                      if (parts.length > 0) parts.push("", "");
                      parts.push(
                        parts.length > 0
                          ? deleteWorkflowStepPointsToOthersIntroAlso
                          : deleteWorkflowStepPointsToOthersIntroOnly,
                        ""
                      );
                      pointedTo.forEach(({ step: s, viaGoto, viaException, viaMenuOption }) => {
                        const types: string[] = [];
                        if (viaGoto) types.push(deleteWorkflowStepRefTypeGoto);
                        if (viaException) types.push(deleteWorkflowStepRefTypeException);
                        if (viaMenuOption) types.push(deleteWorkflowStepRefTypeMenuOption);
                        const detail = types.join(", ");
                        parts.push(
                          `"${s.stepNumber}" (${stageLabel} ${s.stepNumber}). ${detail}.`
                        );
                      });
                    }
                    if (parts.length > 0) {
                      parts.push("", "", deleteWorkflowStepReferencedFinalMessage);
                      return parts.join("\n");
                    }
                    return deleteWorkflowStepDialogContent;
                  })()
                : deleteWorkflowStepDialogContent
            }
            cancelText={
              stepToDelete &&
              (getStepsReferencingStep(stepToDelete, workflow?.workflowSteps).length > 0 ||
                getStepsPointedToByStepWithDetail(
                  stepToDelete,
                  workflow?.workflowSteps
                ).length > 0)
                ? deleteWorkflowStepDialogClose
                : deleteWorkflowStepDialogCancel
            }
            confirmText={deleteWorkflowStepDialogConfirm}
            showConfirmButton={
              !stepToDelete ||
              (getStepsReferencingStep(stepToDelete, workflow?.workflowSteps).length === 0 &&
                getStepsPointedToByStepWithDetail(
                  stepToDelete,
                  workflow?.workflowSteps
                ).length === 0)
            }
            isLoading={isLoadingRemoveWorkflowStep}
          />
        </MDBox>
      </Card>
    </PageContainer>
  );
}
